/**
 * A ticket line's relationship to the price book — linked, deliberately off
 * book, or neither — and the approval gate's answer about it.
 *
 * API side: 0504 (api#1036) added `ticket_line_items.off_book`; 0508 (api#1042)
 * added the per-company approval gate. Casing lines only.
 *
 * ─── off_book HAS THREE STATES ON THE WIRE, NOT TWO ─────────────────────────
 *
 *   absent      "this save says nothing" — an in-place UPDATE keeps the stored value
 *   false       "this line is not marked off book"
 *   true        "deliberately not a price-book item"
 *
 * The editors hold it as `boolean | undefined`, and `undefined` is the first
 * row: JSON drops it, so the key never reaches the API. Defaulting a line read
 * WITHOUT the column (an offline copy cached by a bundle older than 0504) to
 * `false` would send `offBook: false` on its next save and silently undo an
 * office "not in price book" mark. `readOffBook` therefore never coerces —
 * a non-boolean is `undefined`, not `false`.
 *
 * ─── AUTO-LINK: exact name, exactly one item, on leaving the field ──────────
 *
 * The same rule the API applies to AI-drafted lines
 * (`resolveDraftLineRateSheetItemId`) and billing pre-flight's name match:
 * trimmed, case-folded equality with `serviceName`, and exactly one item or
 * nothing. Two items with one name — across sheets as much as on one — is the
 * book's ambiguity, and the link picks the QuickBooks item, so it is never
 * guessed. A row whose unit is already set and differs from the item's is
 * left alone: one name at two units is two prices (TNT's stabbing guide is
 * per_day and per_job).
 *
 * It runs on BLUR, not per keystroke. Linking mid-word would bind "Casing Tong"
 * while the hand is still typing "Casing Tong Rental", and editing never clears
 * a link (lib/rate-sheet-items.ts, note 3), so the wrong row would stick.
 *
 * It sets the LINK ONLY. Rate, unit and description stay as typed: linking a
 * line is a statement about which item it is, not a re-pricing, and a rate the
 * hand typed is not this code's to replace.
 */

import { normalizeUnit } from '../constants/units';
import type { RateSheetItem } from './rate-sheet-items';

const fold = (s: string | null | undefined) => String(s ?? '').trim().toLowerCase();

/** `off_book` from a server row (either casing). Never coerced — see above. */
export function readOffBook(raw: any): boolean | undefined {
  const v = raw?.off_book ?? raw?.offBook;
  return typeof v === 'boolean' ? v : undefined;
}

/** The book item an AI-drafted line resolved to (API 0504), or null. Decided by the API, kept as sent. */
export function readDraftRateSheetItemId(raw: any): string | null {
  const v = raw?.rateSheetItemId ?? raw?.rate_sheet_item_id;
  return typeof v === 'string' && v ? v : null;
}

/**
 * The one book item a typed description names exactly, or null.
 * `rowUnit` blank means the row has no unit yet, which does not disqualify.
 */
export function findExactBookItem(
  items: readonly RateSheetItem[],
  description: string | null | undefined,
  rowUnit?: string | null
): RateSheetItem | null {
  const name = fold(description);
  if (!name) return null;
  const hits = items.filter((i) => fold(i.serviceName) === name);
  if (hits.length !== 1) return null;
  const hit = hits[0];
  const unit = (rowUnit ?? '').trim();
  if (unit) {
    const itemUnit = normalizeUnit(String((hit.unit === 'custom' ? hit.customUnit : hit.unit) ?? ''));
    if (itemUnit && itemUnit !== unit) return null;
  }
  return hit;
}

export type PriceBookLinkState = 'linked' | 'off_book' | 'unlinked';

/** What the row's marker says. Only `unlinked` draws one. */
export function priceBookLinkState(line: {
  rateSheetItemId: string | null;
  offBook?: boolean;
}): PriceBookLinkState {
  if (line.rateSheetItemId) return 'linked';
  if (line.offBook === true) return 'off_book';
  return 'unlinked';
}

// ─── The approval gate (api#1042) ───────────────────────────────────────────

export interface PriceBookUnlinkedLine {
  lineItemId: string;
  description: string;
  lineItemType: string;
  unit: string;
  rate: number;
  quantity: number;
}

function readUnlinkedLines(raw: unknown): PriceBookUnlinkedLine[] | null {
  return Array.isArray(raw) ? (raw as PriceBookUnlinkedLine[]) : null;
}

/**
 * `priceBookWarning` from an approve response body. It is a SIBLING of `data`,
 * so any unwrap of the envelope has already thrown it away — read the body.
 * Null when the API said nothing (gate off with no book, or no unlinked lines),
 * and null from an API that predates 0508.
 */
export function readPriceBookWarning(body: any): PriceBookUnlinkedLine[] | null {
  const lines = readUnlinkedLines(body?.priceBookWarning?.unlinkedLines);
  return lines && lines.length > 0 ? lines : null;
}

/**
 * The 409 the gate answers when the company enforces it. Keyed on `code`, not
 * the status: other approval refusals are also 409s and are not this.
 */
export function readPriceBookRefusal(body: any): { message: string; lines: PriceBookUnlinkedLine[] } | null {
  if (body?.code !== 'price_book_link_required') return null;
  return {
    message: typeof body.error === 'string' ? body.error : 'Some priced lines are not linked to the price book.',
    lines: readUnlinkedLines(body.unlinkedLines) ?? [],
  };
}
